import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/use-auth-store";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  authService,
  type RegisterDto,
  type LoginDto,
  type ChangePasswordDto,
  type UpdateProfileDto,
} from "@/services/auth";
import { UtilsService } from "@/services/utils/utils.service";

const utils = new UtilsService();

export const useRegister = () => {
  return useMutation({
    mutationFn: (userData: RegisterDto) => authService.register(userData),
  });
};

export const useLogin = () => {
  const navigate = useNavigate();
  const { setUser, setRoles } = useAuthStore();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (userData: LoginDto) => authService.login(userData),
    onSuccess: async (response: any) => {
      // Backend sets tokens in HTTP-only cookies automatically
      if (response.accessToken && response.refreshToken) {
        // Get user profile to set auth state
        try {
          const profileResponse = await authService.getProfile();
          const { roles, ...user } = profileResponse;

          // If user has PENDING role, trigger OTP and redirect to verification page
          if (Array.isArray(roles) && roles.includes('PENDING')) {
            try {
              await authService.generateOtp(user.email, 'email_verification');
              toast.success(t('common.otpSentSuccessfully'));
            } catch (e) {
              // If OTP generation fails, still block dashboard and show error
              const errorMessage = utils.extractErrorMessage(e, t('common.otpSendFailed'));
              toast.error(errorMessage);
            }
            // Do not set auth session; redirect to verify email page
            navigate('/auth/verify-email');
            return;
          }

          if (user.status === "ENABLED") {
            setUser(user);
            setRoles(roles);
            toast.success(t("common.loginSuccessful"));
            navigate("/");
          } else {
            toast.error(t("common.accountDisabled"));
          }
        } catch (error) {
          console.error("Failed to get profile after login:", error);
          toast.error(t("common.loginFailedProfile"));
        }
      } else {
        toast.error(t("common.operationFailed"));
      }
    },
    onError: async (error: any) => {
      const errorMessage = utils.extractErrorMessage(error, t("common.operationFailed"));
      toast.error(errorMessage);
    },
  });
};

export const useLogout = () => {
  const navigate = useNavigate();
  const { clearAuth } = useAuthStore();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      clearAuth();
      toast.success(t("common.logoutSuccessful"));
      navigate("/auth/login");
    },
    onError: () => {
      clearAuth();
      toast.info(t("common.loggedOutLocally"));
      navigate("/auth/login");
    },
  });
};

export const useSendOtp = (showToast: boolean = true) => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (email: string) => authService.sendOtp(email),
    onSuccess: (response: any) => {
      if (response && showToast) {
        toast.success(t("common.otpSentSuccessfully"));
      }
    },
    onError: (error: any) => {
      if (showToast) {
        const errorMessage = utils.extractErrorMessage(error, t("common.otpSendFailed"));
        toast.error(errorMessage);
      }
    },
  });
};

// Generate OTP for email verification (or other flows)
export const useGenerateOtp = (showToast: boolean = true) => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ email, type }: { email: string; type?: string }) =>
      authService.generateOtp(email, type ?? 'email_verification'),
    onSuccess: (response: any) => {
      if (response && showToast) {
        toast.success(t('common.otpSentSuccessfully'));
      }
    },
    onError: (error: any) => {
      if (showToast) {
        const errorMessage = utils.extractErrorMessage(error, t('common.otpSendFailed'));
        toast.error(errorMessage);
      }
    },
  });
};

export const useValidateOtp = () => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ email, OTP }: { email: string; OTP: string }) => 
      authService.validateOtp(email, OTP, 'password_reset'),
    onSuccess: (response: any) => {
      if (response) {
        toast.success(t("common.otpValidatedSuccessfully"));
      }
    },
    onError: (error: any) => {
      const errorMessage = utils.extractErrorMessage(error, t("common.otpValidationFailed"));
      toast.error(errorMessage);
    },
  });
};

export const useVerifyEmail = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ email, OTP }: { email: string; OTP: string }) =>
      authService.verifyEmail({ email, OTP }),
    onSuccess: (response: any) => {
      if (response?.success === true) {
        toast.success(t("common.emailVerifiedSuccessfully"));
        navigate("/");
      } else {
        toast.error(response?.message || t("common.emailVerificationFailed"));
      }
    },
    onError: () => {
      toast.error(t("common.emailVerificationFailed"));
    },
  });
};

export const useResetPassword = () => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({
      email,
      OTP,
      newPassword,
    }: {
      email: string;
      OTP: string;
      newPassword: string;
    }) => authService.resetPassword({ email, OTP, newPassword }),
    onSuccess: (response: any) => {
      if (response) {
        toast.success(t("common.passwordResetSuccessful"));
      }
    },
    onError: (error: any) => {
      const errorMessage = utils.extractErrorMessage(error, t("common.operationFailed"));
      toast.error(errorMessage);
    },
  });
};

export const useChangePassword = () => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (data: ChangePasswordDto) => authService.changePassword(data),
    onSuccess: (response: any) => {
      if (response) {
        toast.success(t("common.passwordChangedSuccessfully"));
      }
    },
    onError: (error: any) => {
      const errorMessage = utils.extractErrorMessage(error, t("common.passwordChangeFailed"));
      toast.error(errorMessage);
    },
  });
};

export const useUpdateProfile = () => {
  return useMutation({
    mutationFn: (data: UpdateProfileDto) => authService.updateProfile(data),
  });
};

export const useUploadAvatar = () => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (file: File) => authService.uploadAvatar(file),
    onSuccess: (response: any) => {
      if (response) {
        toast.success(t("common.avatarUploadedSuccessfully"));
      }
    },
    onError: () => {
      toast.error(t("common.avatarUploadFailed"));
    },
  });
};

export const useGetProfile = () => {
  return useQuery({
    queryKey: ["userProfile"],
    queryFn: () => authService.getProfile(),
    enabled: false,
    retry: false,
  });
};
